import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useModal } from '../context/ModalContext';
import CustomSelect from './CustomSelect';

const InvitationsPanel = ({ user, dashboardData, isSuperAdmin = false, activeAgency = null }) => {
  const modal = useModal();
  const [inviteForm, setInviteForm] = useState({
    full_name: '',
    email: '',
    user_type: isSuperAdmin ? 'client' : 'operator',
    organization_id: '',
    brand_id: '',
    agency_id: '',
    agency_name: ''
  });
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [cancellingInvitations, setCancellingInvitations] = useState(new Set());
  const [regeneratingInvitations, setRegeneratingInvitations] = useState(new Set());

  useEffect(() => {
    fetchPendingInvitations();
  }, []);

  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  };

  const safeDate = (dateValue) => {
    if (!dateValue) return 'Never';
    try {
      if (typeof dateValue === 'string') {
        return new Date(dateValue).toLocaleDateString();
      }
      if (dateValue instanceof Date) {
        return dateValue.toLocaleDateString();
      }
      return 'Invalid date';
    } catch {
      return 'Invalid date';
    }
  };

  const fetchPendingInvitations = async () => {
    try {
      const response = await axios.get('/api/super-admin/invitations');
      setPendingInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
    }
  };

  const handleSendInvitation = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    try {
      const user_role_data = {};

      // Get agency context - for admins, use their own agency
      const currentAgencyId = isSuperAdmin
        ? (activeAgency?._id || sessionStorage.getItem('selected_agency_id'))
        : user?.agency_id;
      const currentAgencyName = isSuperAdmin
        ? (activeAgency?.agency_name || sessionStorage.getItem('selectedAgencyName'))
        : user?.agency_name;

      if (inviteForm.user_type === 'client') {
        if (currentAgencyId) {
          user_role_data.agency_id = currentAgencyId;
          user_role_data.agency_name = currentAgencyName || '';
        }
      } else if (inviteForm.user_type === 'admin') {
        // For super admins, use selected agency; for admins, use their own agency
        if (isSuperAdmin && inviteForm.agency_id) {
          user_role_data.agency_id = inviteForm.agency_id;
          user_role_data.agency_name = inviteForm.agency_name || '';
        } else if (currentAgencyId) {
          user_role_data.agency_id = currentAgencyId;
          user_role_data.agency_name = currentAgencyName || '';
        }
      } else if (inviteForm.user_type === 'operator') {
        // For super admins, use selected agency; for admins, use their own agency
        if (isSuperAdmin && inviteForm.agency_id) {
          user_role_data.agency_id = inviteForm.agency_id;
          user_role_data.agency_name = inviteForm.agency_name || '';
        } else if (currentAgencyId) {
          user_role_data.agency_id = currentAgencyId;
          user_role_data.agency_name = currentAgencyName || '';
        }
      }

      const response = await axios.post('/api/super-admin/send-invitation', {
        full_name: inviteForm.full_name,
        email: inviteForm.email,
        user_type: inviteForm.user_type,
        user_role_data
      });

      if (response.data.success) {
        modal.success(`Invitation sent successfully to ${inviteForm.email}!`);
        setInviteForm({
          full_name: '',
          email: '',
          user_type: isSuperAdmin ? 'client' : 'operator',
          organization_id: '',
          brand_id: '',
          agency_id: '',
          agency_name: ''
        });
        fetchPendingInvitations();
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      modal.error(error.response?.data?.error || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvitation = async (token, email) => {
    const result = await modal.confirm(`Are you sure you want to cancel the invitation for ${email}?`, 'Cancel Invitation');
    if (!result) return;

    setCancellingInvitations(prev => new Set([...prev, token]));
    try {
      const response = await axios.post('/api/super-admin/cancel-invitation', {
        token: token
      });
      if (response.data.success) {
        modal.success('Invitation cancelled successfully');
        fetchPendingInvitations();
      }
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      modal.error('Failed to cancel invitation');
    } finally {
      setCancellingInvitations(prev => {
        const next = new Set(prev);
        next.delete(token);
        return next;
      });
    }
  };

  const handleRegenerateInvitation = async (invitation) => {
    setRegeneratingInvitations(prev => new Set([...prev, invitation.token]));
    try {
      const response = await axios.post('/api/super-admin/regenerate-invitation', {
        token: invitation.token,
        agency_id: invitation.agency_id || invitation.user_role_data?.agency_id
      });
      if (response.data.success) {
        modal.success('Invitation regenerated and sent successfully');
        fetchPendingInvitations();
      }
    } catch (error) {
      console.error('Error regenerating invitation:', error);
      modal.error(error.response?.data?.error || 'Failed to regenerate invitation');
    } finally {
      setRegeneratingInvitations(prev => {
        const next = new Set(prev);
        next.delete(invitation.token);
        return next;
      });
    }
  };

  // Determine which user types can be invited
  // Admins can only invite operators and clients (not other admins)
  const availableUserTypes = isSuperAdmin
    ? [
        { value: 'admin', label: 'Admin' },
        { value: 'operator', label: 'Operator' },
        { value: 'client', label: 'Client' }
      ]
    : [
        { value: 'operator', label: 'Operator' },
        { value: 'client', label: 'Client' }
      ];

  return (
    <div className="content-section">
      <div className="invitations-grid">
        <div className="admin-card has-form">
          <h3>Send New Invitation</h3>
          <form onSubmit={handleSendInvitation} className="invite-form">
            <div className="form-group">
              <label htmlFor="invite-full-name">Full Name</label>
              <input
                type="text"
                id="invite-full-name"
                value={inviteForm.full_name}
                onChange={(e) => setInviteForm({...inviteForm, full_name: e.target.value})}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="invite-email">Email Address</label>
              <input
                type="email"
                id="invite-email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                placeholder="user@gmail.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="invite-user-type">User Type</label>
              <CustomSelect
                  id="invite-user-type"
                  value={inviteForm.user_type}
                  onChange={(e) => setInviteForm({
                    ...inviteForm,
                    user_type: e.target.value,
                    agency_id: '',
                    agency_name: '',
                    organization_id: '',
                    brand_id: ''
                  })}
                >
                  {availableUserTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </CustomSelect>
            </div>

            {/* Agency selector - only for super admins inviting admin/operator */}
            {isSuperAdmin && (inviteForm.user_type === 'admin' || inviteForm.user_type === 'operator') && (
              <div className="form-group">
                <label htmlFor="invite-agency">Agency *</label>
                <CustomSelect
                    id="invite-agency"
                    value={inviteForm.agency_id}
                    onChange={(e) => {
                      const selectedAgency = dashboardData?.data?.all_agencies?.find(c => (c._id || c.id) === e.target.value);
                      setInviteForm({
                        ...inviteForm,
                        agency_id: e.target.value,
                        agency_name: selectedAgency?.agency_name || ''
                      });
                    }}
                    required
                  >
                    <option value="">Select Agency</option>
                    {dashboardData?.data?.all_agencies?.map(agency => (
                      <option key={safeString(agency._id || agency.id)} value={safeString(agency._id || agency.id)}>
                        {safeString(agency.agency_name)}
                      </option>
                    ))}
                  </CustomSelect>
                <small className="form-hint">{inviteForm.user_type === 'admin' ? 'Admin' : 'Operator'} will be added to this agency</small>
              </div>
            )}

            {/* For admins inviting operator - show their agency (read-only) */}
            {!isSuperAdmin && inviteForm.user_type === 'operator' && (
              <div className="form-group">
                <label>Agency</label>
                <input
                  type="text"
                  value={user?.agency_name || 'Your Agency'}
                  disabled
                  className="form-control-disabled"
                />
                <small className="form-hint">Operator will be added to your agency</small>
              </div>
            )}


            <button
              type="submit"
              className="submit-btn"
              disabled={inviteLoading}
            >
              {inviteLoading ? 'Sending...' : 'Send Invitation'}
            </button>
          </form>
        </div>

        <div className="admin-card">
          <h3>Pending Invitations ({pendingInvitations.length})</h3>
          <div className="invitations-list">
            {pendingInvitations.length === 0 ? (
              <p className="no-invitations">No pending invitations</p>
            ) : (
              pendingInvitations.map((invitation) => {
                const isExpired = invitation.is_expired || (invitation.expires_at && new Date(invitation.expires_at) < new Date());
                return (
                  <div key={safeString(invitation._id)} className="invitation-item">
                    <div className="invitation-info">
                      <div className="invitation-email">{safeString(invitation.email)}</div>
                      <div className="dashboard-invitation-details">
                        <span className="user-type">{safeString(invitation.user_type).replace('_', ' ')}</span>
                        <span className="created-date">
                          Sent: {safeDate(invitation.created_at)}
                        </span>
                        <span className="expires-date">
                          Expires: {safeDate(invitation.expires_at)}
                        </span>
                      </div>
                    </div>
                    <div className="invitation-status">
                      <span className={`status-badge ${isExpired ? 'expired' : 'pending'}`}>
                        {isExpired ? 'Expired' : 'Pending'}
                      </span>
                      {isExpired ? (
                        <button
                          className="regenerate-invitation-btn"
                          onClick={() => handleRegenerateInvitation(invitation)}
                          disabled={regeneratingInvitations.has(safeString(invitation.token))}
                          title="Regenerate this invitation"
                        >
                          <i className="fas fa-refresh"></i>
                          {regeneratingInvitations.has(safeString(invitation.token)) ? (
                            <> Regenerating...</>
                          ) : (
                            <> Regenerate</>
                          )}
                        </button>
                      ) : (
                        <button
                          className="cancel-invitation-btn"
                          onClick={() => handleCancelInvitation(safeString(invitation.token), safeString(invitation.email))}
                          disabled={cancellingInvitations.has(safeString(invitation.token))}
                          title="Cancel this invitation"
                        >
                          {cancellingInvitations.has(safeString(invitation.token)) ? (
                            <>Cancelling...</>
                          ) : (
                            <>Cancel</>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationsPanel;
